declare const DEV_DOMAIN: string | undefined;
declare const APP_STAGE: string;
declare const OVERRIDE_APP_URL_DOMAIN: string;
declare const OVERRIDE_TORTUGA_GRAPHQL_URL_DOMAIN: string;
declare const OVERRIDE_CHAMPAIGN_URL_DOMAIN: string;

function localOffsetSuffix() {
  if (typeof window === 'undefined') {
    return '';
  }

  const [subdomain] = window.location.hostname.split('.');
  const match = subdomain?.match(/-(\d+)$/);
  return match ? `-${match[1]}` : '';
}

function withLocalOffset(hostname: string) {
  const suffix = localOffsetSuffix();
  if (!suffix) {
    return hostname;
  }

  return hostname.replace(/^app(?=\.)/, `app${suffix}`);
}

function localDevUrlWithSubdomain(subdomain: string) {
  if (typeof window === 'undefined') {
    return '';
  }

  const { hostname, port } = window.location;
  const hostParts = hostname.split('.');
  const suffix = localOffsetSuffix();
  const nextSubdomain = `${subdomain}${suffix}`;

  if (hostParts.length === 2) {
    return `//${nextSubdomain}.${hostname}${port ? `:${port}` : ''}`;
  }

  hostParts[0] = nextSubdomain;
  return `//${hostParts.join('.')}${port ? `:${port}` : ''}`;
}

function urlWithSubdomain(subdomain: string) {
  const { host } = window.location;
  const splitHost = host.split('.');

  if (splitHost.length === 2) {
    // No subdomain
    return `//${subdomain}.${splitHost.join('.')}`;
  }
  // With subdomain
  splitHost[0] = subdomain;
  return `//${splitHost.join('.')}`;
}

const devDomain = () => (typeof DEV_DOMAIN !== 'undefined' && DEV_DOMAIN !== '' ? DEV_DOMAIN : 'mutiny.local');
const appUrlDomainOverride = () =>
  typeof OVERRIDE_APP_URL_DOMAIN !== 'undefined' && OVERRIDE_APP_URL_DOMAIN !== '' ? OVERRIDE_APP_URL_DOMAIN : '';
const champaignUrlDomainOverride = () =>
  typeof OVERRIDE_CHAMPAIGN_URL_DOMAIN !== 'undefined' && OVERRIDE_CHAMPAIGN_URL_DOMAIN !== ''
    ? OVERRIDE_CHAMPAIGN_URL_DOMAIN
    : '';
const tortugaGraphQLUrlDomainOverride = () =>
  typeof OVERRIDE_TORTUGA_GRAPHQL_URL_DOMAIN !== 'undefined' && OVERRIDE_TORTUGA_GRAPHQL_URL_DOMAIN !== ''
    ? OVERRIDE_TORTUGA_GRAPHQL_URL_DOMAIN
    : '';

export const wwwUrl = () => urlWithSubdomain('www');
export const previewUrl = () => urlWithSubdomain('preview');
export const appUrl = () => urlWithSubdomain('app');
export const proxyUrl = () => {
  switch (APP_STAGE) {
    case 'demo':
      return 'https://peek-achoo-demo.com';
    case 'production':
      return 'https://peek-achoo.com';
    case 'staging':
      return 'https://peek-achoo-staging.com';
    default:
      return 'https://peek-achoo.local';
  }
};
export const clientUrl = () => {
  switch (APP_STAGE) {
    case 'demo':
      return '//client-registry.mutinyhq-demo.com';
    case 'production':
      return '//client-registry.mutinycdn.com';
    case 'staging':
      return '//client-registry.mutinycdn-staging.com';
    default:
      return `//client-registry.${devDomain()}`;
  }
};
export const appUrlDomain = () => {
  const searchParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  if (['development', 'staging'].includes(APP_STAGE) && searchParams.has('mutiny_app_url')) {
    const override = searchParams.get('mutiny_app_url');
    if (!override) {
      return override;
    }

    if (APP_STAGE === 'development') {
      const normalized = override.replace(/^https?:\/\//, '//');
      if (normalized.startsWith('//')) {
        const host = normalized.slice(2);
        return `//${withLocalOffset(host)}`;
      }
    }

    return override;
  }

  const overrideAppUrlDomain = appUrlDomainOverride();
  if (APP_STAGE === 'development') {
    const localAppHost = localDevUrlWithSubdomain('app');
    if (localAppHost) {
      return localAppHost;
    }
  }

  if (APP_STAGE === 'development' && overrideAppUrlDomain) {
    return `//${overrideAppUrlDomain}`;
  }

  switch (APP_STAGE) {
    case 'demo':
      return '//app.mutinyhq-demo.com';
    case 'production':
      return '//app.mutinyhq.com';
    case 'staging':
      return '//app.mutinyhq-staging.com';
    default:
      return `//app${localOffsetSuffix()}.${devDomain()}`;
  }
};

export const champaignUrlDomain = () => {
  const localChampaignHost =
    localDevUrlWithSubdomain('champaign') || `//champaign${localOffsetSuffix()}.${devDomain()}`;

  switch (APP_STAGE) {
    case 'demo':
      return '//campaigns.mutinyhq-demo.com';
    case 'production':
      return '//campaigns.mutinyhq.com';
    case 'staging':
      return '//campaigns.mutinyhq-staging.com';
    default:
      return champaignUrlDomainOverride() ? `//${champaignUrlDomainOverride()}` : localChampaignHost;
  }
};

export const agentUrlDomain = () => {
  switch (APP_STAGE) {
    case 'production':
      return '//agent.mutinyhq.com';
    case 'staging':
      return '//agent.mutinyhq-staging.com';
    case 'demo':
      return '//agent.mutinyhq-demo.com';
    default:
      return champaignUrlDomain();
  }
};

export const tortugaGraphQLUrlDomain = () => {
  const localTortugaHost =
    localDevUrlWithSubdomain('tortuga-ext') || `//tortuga-ext${localOffsetSuffix()}.${devDomain()}`;

  switch (APP_STAGE) {
    case 'production':
      return '//tortuga-ext.mutinyhq.com';
    case 'demo':
    case 'staging':
      return '//tortuga-ext.mutinyhq-staging.com';
    default:
      return tortugaGraphQLUrlDomainOverride() ? `//${tortugaGraphQLUrlDomainOverride()}` : localTortugaHost;
  }
};
